/**
 * Derives a first-touch source from a referrer when no attribution cookie exists.
 *
 * The marketing site writes `tt_attr` for anyone who lands there, so visitors who
 * arrive via www already have a first touch by the time they reach this app. What
 * this covers is the visitor who never touches the marketing site at all: they
 * search, click a result for assistant.thisandthat.chat, and sign up. Before this,
 * both attribution columns were blank for them, which matters more as our own
 * pages start ranking.
 *
 * Kept in sync with the equivalent block in the marketing site's BaseLayout.astro.
 * Two copies exist because that one is an inline script on a different origin with
 * no build step; if the labels diverge the funnel gets two names for one channel.
 */

const APEX = 'thisandthat.chat';

const AI_HOSTS: ReadonlyArray<readonly [string, string]> = [
  ['chatgpt.com', 'ai-chatgpt'],
  ['chat.openai', 'ai-chatgpt'],
  ['claude.ai', 'ai-claude'],
  ['perplexity.ai', 'ai-perplexity'],
  ['gemini.google', 'ai-gemini'],
  ['copilot.microsoft', 'ai-copilot'],
];

const SEARCH_HOSTS: ReadonlyArray<readonly [string, string]> = [
  ['google.', 'organic-google'],
  ['bing.com', 'organic-bing'],
  ['duckduckgo', 'organic-duckduckgo'],
  ['yahoo.', 'organic-yahoo'],
  ['ecosia.', 'organic-ecosia'],
  ['brave.com', 'organic-brave'],
  ['startpage', 'organic-startpage'],
  ['qwant', 'organic-qwant'],
  ['baidu', 'organic-baidu'],
  ['yandex', 'organic-yandex'],
];

/**
 * Returns null for our own domains and for anything unparseable, so a first touch
 * is left unset rather than recorded wrongly. Internal navigation must never look
 * like a new arrival.
 */
export function referrerSource(referrer: string): string | null {
  if (!referrer) return 'direct';

  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (!host) return null;

  if (host === APEX || host.endsWith(`.${APEX}`)) return null;

  for (const [needle, label] of AI_HOSTS) {
    if (host.includes(needle)) return label;
  }
  for (const [needle, label] of SEARCH_HOSTS) {
    if (host.includes(needle)) return label;
  }

  return `referral-${host.startsWith('www.') ? host.slice(4) : host}`;
}
